import { useCallback, useState } from "react";

import { useToast } from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";
import pluralize from "pluralize";
import { useQueryClient } from "react-query";

import { useUser } from "src/contexts/user-context";
import {
  type RunVisualBackgroundQueryVariables,
  useCancelPreviewQueryJobMutation,
  useVisualQueryBackgroundResultQuery,
} from "src/graphql";
import { useRunVisualBackgroundQuery } from "src/graphql";
import type { VisualQueryFilter } from "src/types/visual";
import {
  type Query,
  useLastUsedQuery,
} from "src/components/audiences/use-last-used-query";
import { useFormErrorContext } from "src/contexts/form-error-context";
import { commaNumber } from "./numbers";
import type { ResourcePermissionInput } from "src/components/permission/use-resource-permission";
import { stripMarkdownLinks } from "src/ui/markdown";

interface UseCalculateAudienceSizeInput {
  audienceId: string | undefined;
  householdModelId?: string;
  accountModelId?: string;
  isFastQuery: boolean;
  parentModelId: string;
  sourceId: string;
  visualQueryFilter: VisualQueryFilter;
  subsetIds: string[] | undefined;
  destinationId: string | undefined;
  // When true, the backend will attach the caller's default (accessible)
  // subsets for each required subset group on the parent model. Use this for
  // parent-model previews with no saved audience, where the UI doesn't expose a
  // subset picker. Ignored unless audienceId and subsetIds are both undefined.
  // Matches getOrComputeAudienceSize behavior for draft audiences.
  useDefaultSubsets?: boolean;
}

export type UseCalculateAudienceSizeOutput = {
  audienceSize: {
    calculatedAt: string;
    count: number;
    isCached: boolean;
    isEstimate: boolean;
  } | null;
  householdSize: {
    calculatedAt: string;
    count: number;
    isCached: boolean;
    isEstimate: boolean;
  } | null;
  accountSize: {
    calculatedAt: string;
    count: number;
    isCached: boolean;
    isEstimate: boolean;
  } | null;
  isLoading: boolean;
  lastUsedQuery: Query | null;
  calculateSizes: (opts?: CalculateSizesOptions) => Promise<void>;
  cancelCalculateAudienceSize: () => Promise<void>;
  updateAudienceSize: (
    updatedSize: {
      calculatedAt?: string;
      count: number;
      isCached?: boolean;
      isEstimate: boolean;
    } | null,
    query?: Query,
  ) => void;
  updateHouseholdSize: (
    updatedSize: {
      calculatedAt?: string;
      count: number;
      isCached?: boolean;
      isEstimate: boolean;
    } | null,
  ) => void;
  updateAccountSize: (
    updatedSize: {
      calculatedAt?: string;
      count: number;
      isCached?: boolean;
      isEstimate: boolean;
    } | null,
  ) => void;
};

export type AudienceSizeData = {
  count: number;
  isCached: boolean;
  isEstimate: boolean;
  calculatedAt: string;
};

export type CalculateSizesOptions = {
  /** Invoked after validation passes, immediately before background queries start. */
  onQueryStart?: () => void;
  /**
   * Skip the person count and only compute household/account sizes. Used by
   * realtime audiences, whose person count comes from the cached size rather
   * than an on-demand count, but which still want related-model tab counts.
   */
  skipPeople?: boolean;
};

const getUserErrorMessage = (errMessage: string): string => {
  if (errMessage == "Error: relationship does not exist") {
    return errMessage + ". The underlying model may have been changed";
  }

  return stripMarkdownLinks(errMessage);
};

interface UseSizeQueryOptions {
  queryVariables: RunVisualBackgroundQueryVariables;
  parentModelId: string;
  visualQueryFilter: VisualQueryFilter;
  onSuccess: (result: { count: number; isEstimate: boolean } | null) => void;
  toastId: string;
  toastTitle: string;
}

/**
 * A reusable hook for running and polling a background query for size calculation.
 */
const useSizeQuery = ({
  queryVariables,
  parentModelId,
  visualQueryFilter,
  onSuccess,
  toastId,
  toastTitle,
}: UseSizeQueryOptions) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [jobId, setJobId] = useState<string | null>(null);
  const [variables, setVariables] =
    useState<null | RunVisualBackgroundQueryVariables>(null);
  const [shouldPoll, setShouldPoll] = useState(false);

  const runBackgroundQuery = useRunVisualBackgroundQuery(queryVariables, {
    enabled: false,
    cacheTime: 0,
  });

  // Polling for results
  useVisualQueryBackgroundResultQuery(
    {
      jobId: jobId ?? "",
      page: 0,
      filter: visualQueryFilter,
      parentModelId,
    },
    {
      enabled: Boolean(jobId) && Boolean(shouldPoll),
      refetchInterval: 500,
      // Disable caching the query results.
      cacheTime: 0,
      onError: (error) => {
        captureException(error);
        setShouldPoll(false);
        setJobId(null);

        toast({
          id: toastId,
          title: toastTitle,
          message: stripMarkdownLinks(error.message),
          variant: "error",
        });
      },
      onSuccess: (data) => {
        if (!data?.visualQueryBackgroundResult) {
          return;
        }

        if (
          data.visualQueryBackgroundResult.__typename ===
          "SuccessfulQueryResponse"
        ) {
          onSuccess(
            data.visualQueryBackgroundResult.numRowsWithoutLimit === null
              ? null
              : {
                  count: Number(
                    data.visualQueryBackgroundResult.numRowsWithoutLimit,
                  ),
                  isEstimate: Boolean(
                    data.visualQueryBackgroundResult.usedSampledModels,
                  ),
                },
          );
        } else {
          toast({
            id: toastId,
            title: toastTitle,
            message: getUserErrorMessage(
              data.visualQueryBackgroundResult.error,
            ),
            variant: "error",
          });
        }

        setShouldPoll(false);
        setJobId(null);
      },
    },
  );

  const runQuery = async () => {
    setShouldPoll(true);
    setVariables(queryVariables);

    try {
      const result = await runBackgroundQuery.refetch();
      const newJobId = result.data?.visualQueryBackground.jobId ?? null;

      if (!newJobId) {
        setShouldPoll(false);

        if (result.error) {
          toast({
            id: toastId,
            title: toastTitle,
            message: stripMarkdownLinks(result.error.message),
            variant: "error",
          });
          captureException(result.error);
        }
        return;
      }

      setJobId(newJobId);
    } catch (error) {
      setShouldPoll(false);
      setJobId(null);

      toast({
        id: toastId,
        title: `Failed to calculate ${toastTitle.toLowerCase()}`,
        message: "Please try again",
        variant: "error",
      });

      captureException(error);
    }
  };

  const cancelQuery = async (cancelBackgroundQuery: {
    mutate: (variables: { jobId: string }) => void;
  }) => {
    if (variables !== null) {
      const queryKey = useRunVisualBackgroundQuery.getKey(variables);
      queryClient.cancelQueries([queryKey]);
      setShouldPoll(false);
      if (jobId) {
        cancelBackgroundQuery.mutate({ jobId });
      }
    }
  };

  return {
    isLoading: shouldPoll,
    runQuery,
    cancelQuery,
    variables,
  };
};

/**
 * A hook that can be used to run a count-only audience preview.
 *
 * It exposes functions to update stored audience size since breakdowns, overlaps
 * and previews in `useModelRun` can return an audience size.
 *
 * Now supports counting households and accounts in parallel when householdModelId
 * or accountModelId are provided.
 */
export const useCalculateAudienceSize = ({
  audienceId,
  parentModelId,
  householdModelId,
  accountModelId,
  sourceId,
  visualQueryFilter,
  subsetIds,
  isFastQuery,
  destinationId,
  useDefaultSubsets,
}: UseCalculateAudienceSizeInput): UseCalculateAudienceSizeOutput => {
  const { featureFlags } = useUser();
  const { toast } = useToast();
  const { hasValidationErrors } = useFormErrorContext();

  const [
    audienceSize,
    // The audience size should be set using the `updateAudienceSize ` wrapper function
    // since we have a few callbacks that might need to be run when the audience size is recalculated
    _setAudienceSize,
  ] = useState<AudienceSizeData | null>(null);
  const [lastUsedQuery, setLastUsedQuery] = useLastUsedQuery(null);

  const [householdSize, _setHouseholdSize] = useState<AudienceSizeData | null>(
    null,
  );

  const [accountSize, _setAccountSize] = useState<AudienceSizeData | null>(
    null,
  );

  const updateAudienceSize = (
    updatedAudienceSize: {
      count: number;
      isEstimate: boolean;
      isCached?: boolean;
      calculatedAt?: string;
    } | null,
    query?: Query,
  ) => {
    // Anything can set the audience size. We need to track which filter was
    // run on the last audience size update.

    if (updatedAudienceSize) {
      _setAudienceSize({
        ...updatedAudienceSize,

        // These are only relevant for cached audience sizes.
        // We provide defaults so that unrelated callers
        // of this function don't need to worry about them.
        calculatedAt:
          updatedAudienceSize.calculatedAt ?? new Date().toISOString(),
        isCached: updatedAudienceSize.isCached ?? false,
      });
    } else {
      _setAudienceSize(null);
    }

    if (query) {
      setLastUsedQuery(query);
    }
  };

  const updateHouseholdSize = (
    updatedSize: {
      count: number;
      isEstimate: boolean;
      isCached?: boolean;
      calculatedAt?: string;
    } | null,
  ) => {
    if (updatedSize) {
      _setHouseholdSize({
        ...updatedSize,
        calculatedAt: updatedSize.calculatedAt ?? new Date().toISOString(),
        isCached: updatedSize.isCached ?? false,
      });
    } else {
      _setHouseholdSize(null);
    }
  };

  const updateAccountSize = (
    updatedSize: {
      count: number;
      isEstimate: boolean;
      isCached?: boolean;
      calculatedAt?: string;
    } | null,
  ) => {
    if (updatedSize) {
      _setAccountSize({
        ...updatedSize,
        calculatedAt: updatedSize.calculatedAt ?? new Date().toISOString(),
        isCached: updatedSize.isCached ?? false,
      });
    } else {
      _setAccountSize(null);
    }
  };

  const baseVariables = {
    audienceId,
    filter: visualQueryFilter,
    parentModelId,
    sourceId,
    countOnly: true,
    disableRowCounter: featureFlags?.sql_row_counter_disabled,
    subsetIds,
    useDefaultSubsets,
    useSampledModels: isFastQuery,
    destinationId,
  };

  const peopleQuery = useSizeQuery({
    queryVariables: baseVariables,
    parentModelId,
    visualQueryFilter,
    onSuccess: (result) => {
      updateAudienceSize(result, {
        filter: visualQueryFilter,
        subsetIds,
        isFastQuery,
        destinationId,
      });
    },
    toastId: "calculate-size-query",
    toastTitle: "Size calculation failed",
  });

  const householdQuery = useSizeQuery({
    queryVariables: {
      ...baseVariables,
      relatedModelId: householdModelId?.toString(),
    },
    parentModelId,
    visualQueryFilter,
    onSuccess: (result) => {
      updateHouseholdSize(result);
    },
    toastId: "calculate-household-size-query",
    toastTitle: "Household size calculation failed",
  });

  const accountQuery = useSizeQuery({
    queryVariables: {
      ...baseVariables,
      relatedModelId: accountModelId?.toString(),
    },
    parentModelId,
    visualQueryFilter,
    onSuccess: (result) => {
      updateAccountSize(result);
    },
    toastId: "calculate-account-size-query",
    toastTitle: "Account size calculation failed",
  });

  const calculateSizes = useCallback(
    async (opts?: CalculateSizesOptions) => {
      if (hasValidationErrors()) {
        toast({
          id: "calculate-size-query",
          title: "Unable to calculate audience size",
          message: "Please check your query and try again.",
          variant: "error",
        });
        return;
      }

      opts?.onQueryStart?.();

      const queryPromises: Promise<unknown>[] = [];

      if (!opts?.skipPeople) {
        queryPromises.push(
          peopleQuery.runQuery().then(() => {
            setLastUsedQuery({
              filter: visualQueryFilter,
              subsetIds,
              isFastQuery,
              destinationId,
            });
          }),
        );
      }

      if (householdModelId) {
        queryPromises.push(householdQuery.runQuery());
      }

      if (accountModelId) {
        queryPromises.push(accountQuery.runQuery());
      }

      // Wait for all queries to complete
      await Promise.all(queryPromises);
    },
    [
      hasValidationErrors,
      toast,
      peopleQuery,
      householdModelId,
      householdQuery,
      accountModelId,
      accountQuery,
      setLastUsedQuery,
      visualQueryFilter,
      subsetIds,
      isFastQuery,
      destinationId,
    ],
  );

  const cancelBackgroundQuery = useCancelPreviewQueryJobMutation({
    onSuccess: () => {
      // set `onSuccess` as a noop so running the mutation
      // does not invalidate cache on cancellations
    },
  });

  const cancelQuery = async () => {
    await Promise.all([
      peopleQuery.cancelQuery(cancelBackgroundQuery),
      householdQuery.cancelQuery(cancelBackgroundQuery),
      accountQuery.cancelQuery(cancelBackgroundQuery),
    ]);

    // Show toast if any queries were canceled
    if (
      peopleQuery.variables !== null ||
      householdQuery.variables !== null ||
      accountQuery.variables !== null
    ) {
      toast({
        id: "calculate-size-query",
        title: "Query canceled",
        variant: "info",
      });
    }
  };

  return {
    audienceSize,
    householdSize,
    accountSize,
    isLoading:
      peopleQuery.isLoading ||
      householdQuery.isLoading ||
      accountQuery.isLoading,
    lastUsedQuery,

    calculateSizes,
    cancelCalculateAudienceSize: cancelQuery,
    updateAudienceSize,
    updateHouseholdSize,
    updateAccountSize,
  };
};

export const displayAudienceSize = (
  audienceSize: AudienceSizeData,
  label?: string,
) =>
  `${audienceSize.isEstimate ? "~" : ""}${commaNumber(
    audienceSize.count,
  )} ${pluralize(label ?? "member", audienceSize.count)}`;

export const getAudienceSizePermission = ({
  parentModelId,
  modelId,
}: {
  parentModelId?: number | string;
  modelId?: string | number | null;
}): ResourcePermissionInput<"model"> => ({
  v2: {
    resource: "model",
    grant: "can_create",
    creationOptions: {
      type: "audience",
      parentModelId: parentModelId?.toString() ?? "",
    },
  },
});
